// Подать заявку-расход с фотографией чека. Сценарий 48.
//
// Это главное действие человека в поле: отчитаться за трату. Всё остальное в подотчёте —
// следствие того, что этот путь работает.
//
// Файл уходит ОТДЕЛЬНЫМ запросом до создания заявки. Так при обрыве теряется загрузка, а не
// отчёт: человек видит, что чек доехал, ещё до отправки самой заявки.
//
// Панель не закрывается сама после успеха: в ответе приходит новое состояние корзин, и именно
// ради него человек сюда и заходил. Плюс кнопка «Подать ещё одну»: за один заход в поле
// набирается несколько чеков, и заставлять открывать панель заново — лишний путь.

import type { FormEvent } from 'react';
import { useRef, useState } from 'react';

import type { ApiError } from '@/shared/errors/api-error';
import { uploadFile, ошибкаДаты } from '@/shared/api/endpoints/accountable';
import { newIdempotencyKey } from '@/shared/api/idempotency';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks } from '@/shared/money/format';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';
import { useLegalEntitiesQuery } from '@/shared/org/queries';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FilePicker } from '@/shared/ui/file-picker';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';
import * as Switch from '@/shared/ui/switch';

import { useЛюдиДляВыбора } from '@/shared/people/lookup';
import { useCategories, useCreateClaim } from '../queries';

const CREATE_PERMISSION = 'core.accountable.claim_create';

export function NewClaimPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, CREATE_PERMISSION);
  const категории = useCategories();
  const юрлица = useLegalEntitiesQuery();
  const операция = useCreateClaim();

  const [категория, setКатегория] = useState(NO_VALUE);
  const [юрлицо, setЮрлицо] = useState(NO_VALUE);
  const [наименование, setНаименование] = useState('');
  const [сумма, setСумма] = useState('');
  const [дата, setДата] = useState(todayIso);
  const [файл, setФайл] = useState<File | null>(null);
  const [безЧека, setБезЧека] = useState(false);
  const [ошибкаСуммы, setОшибкаСуммы] = useState<string | undefined>(undefined);
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [грузим, setГрузим] = useState(false);
  // Засов от второй отправки. Ref, а не состояние: состояние обновляется к следующей отрисовке,
  // а между двумя нажатиями подряд её не происходит — оба читают «не идёт». Ключ операции
  // спасает от второй ЗАЯВКИ, но не от второй загрузки чека и не от двух запросов подряд.
  const идёт = useRef(false);
  const [ключ, setКлюч] = useState(newIdempotencyKey);
  const [получатель, setПолучатель] = useState(NO_VALUE);
  const [поискПолучателя, setПоискПолучателя] = useState('');

  const итог = операция.data;
  // Отказ по дате живёт ПОД полем даты, а общей плашкой внизу не дублируется: одна и та же
  // фраза в двух местах читается как две разные ошибки, и человек ищет вторую.
  const ошибкаДатыПоля = ошибкаДаты(операция.error);
  const выбранная = (категории.data?.items ?? []).find((строка) => строка.id === категория);
  const нуженПолучатель = выбранная?.needs_recipient ?? false;
  // Людей спрашиваем только когда получатель нужен: иначе это запрос ради поля, которого на
  // экране нет.
  const люди = useЛюдиДляВыбора(нуженПолучатель ? поискПолучателя : undefined);

  async function отправить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!можно) {
      setЗапрет('Подавать заявки может подотчётное лицо и финансовый отдел.');
      return;
    }
    if (идёт.current || операция.isPending || грузим) {
      setЗапрет('Заявка уже отправляется, ждём ответ сервера.');
      return;
    }
    if (категория === NO_VALUE || юрлицо === NO_VALUE) {
      setЗапрет('Выберите категорию расхода и юридическое лицо.');
      return;
    }
    if (наименование.trim() === '') {
      setЗапрет('Напишите, за что расход: по названию финотдел его и разбирает.');
      return;
    }
    const разбор = parseRublesToKopecks(сумма);
    if (!разбор.ok) {
      setОшибкаСуммы(moneyErrorText(разбор.reason, 'сумма расхода'));
      return;
    }
    if (файл === null && !безЧека) {
      setЗапрет(
        'Приложите снимок чека либо отметьте «чека нет». Расход без подтверждения проходит, но это осознанный выбор, а не пропуск.',
      );
      return;
    }
    if (нуженПолучатель && получатель === NO_VALUE) {
      setЗапрет(
        `Категория «${выбранная?.title}» ${
          выбранная?.creates_debt === true
            ? 'заводит получателю долг'
            : 'попадает получателю в ведомость'
        }, поэтому нужно имя. Выберите, кому вы отдали деньги или вещи.`,
      );
      return;
    }

    setОшибкаСуммы(undefined);
    setЗапрет(null);
    идёт.current = true;

    let файлы: string[] = [];
    if (файл !== null) {
      setГрузим(true);
      try {
        const загруженный = await uploadFile(файл, 'receipt');
        файлы = [загруженный.id];
      } catch (ошибка) {
        setГрузим(false);
        идёт.current = false;
        setЗапрет(
          ошибка instanceof Error
            ? `Снимок чека не загрузился: ${ошибка.message}`
            : 'Снимок чека не загрузился.',
        );
        return;
      }
      setГрузим(false);
    }

    операция.mutate(
      {
        body: {
          accountable_person_id: id,
          expense_category_id: категория,
          legal_entity_id: юрлицо,
          title: наименование.trim(),
          amount: разбор.value,
          spent_at: дата === '' ? null : дата,
          no_receipt: файл === null,
          // Получатель уходит только там, где он нужен: у категории без признака сервер его
          // не ждёт, и подставленный опознаватель завёл бы долг человеку ни за что.
          recipient_employee_id: нуженПолучатель && получатель !== NO_VALUE ? получатель : null,
          receipt_file_ids: файлы,
        },
        idempotencyKey: ключ,
      },
      {
        onSuccess: () => {
          setКлюч(newIdempotencyKey());
          setНаименование('');
          setСумма('');
          setФайл(null);
          setБезЧека(false);
          setПолучатель(NO_VALUE);
          setПоискПолучателя('');
        },
        // Засов снимается и на успехе, и на отказе: после отказа человек правит поле и подаёт
        // заново, и запертая форма выглядела бы как зависшая программа.
        onSettled: () => {
          идёт.current = false;
        },
      },
    );
  }

  return (
    <form className='flex flex-col gap-4' onSubmit={(событие) => void отправить(событие)}>
      <SelectField
        id='claim-category'
        label='Категория расхода'
        required
        value={категория}
        placeholder='выберите категорию'
        pending={категории.isPending}
        failed={категории.isError}
        failedError={категории.error as ApiError | null}
        emptyNote='Категорий расхода в справочнике нет. Их заводит администратор в настройках — без категории заявку не подать.'
        options={(категории.data?.items ?? []).map((строка) => ({
          value: строка.id,
          label: строка.title,
        }))}
        onChange={setКатегория}
        hint={
          нуженПолучатель
            ? 'Этой категорией заводится долг получателю, поэтому нужно имя человека.'
            : undefined
        }
      />

      {/* Получатель появляется ТОЛЬКО у категорий, которым он нужен: у остальных это лишнее
          поле, которое человек читает и не понимает, зачем оно. Признак приходит со
          справочником категорий, а не зашит списком кодов. */}
      {нуженПолучатель && (
        <>
          <TextField
            id='claim-recipient-search'
            label='Поиск получателя по фамилии'
            value={поискПолучателя}
            onChange={setПоискПолучателя}
            placeholder='Старцев'
            hint='Список короткий: показываются первые пятьдесят по фамилии.'
          />
          <SelectField
            id='claim-recipient'
            label='Кому выдали'
            required
            value={получатель}
            onChange={setПолучатель}
            options={(люди.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.display_name,
            }))}
            placeholder='выберите человека'
            pending={люди.isPending}
            failed={люди.isError}
            failedError={люди.error as ApiError | null}
            emptyNote='По этой фамилии никого не нашлось. Проверьте написание либо очистите поиск.'
            hint={
              выбранная?.creates_debt === true
                ? 'Этому человеку заведётся долг на сумму заявки, и он удержится из его ближайшей ведомости.'
                : 'Сумма попадёт этому человеку в ведомость.'
            }
          />
        </>
      )}

      <TextField
        id='claim-title'
        label='За что'
        required
        value={наименование}
        onChange={setНаименование}
        placeholder='Развоз охранников Коротчаево'
      />

      <TextField
        id='claim-amount'
        label='Сумма'
        required
        value={сумма}
        onChange={setСумма}
        placeholder='1 200'
        error={ошибкаСуммы}
      />

      <DateField
        id='claim-date'
        label='Когда потратили'
        value={дата}
        onChange={setДата}
        error={ошибкаДатыПоля}
        hint='День траты, а не день отчёта. По нему расход попадает в нужный расчёт: чек с вахты, привезённый через месяц, должен лечь в тот месяц, когда деньги потратили.'
      />

      <SelectField
        id='claim-entity'
        label='Юридическое лицо'
        required
        value={юрлицо}
        placeholder='выберите юрлицо'
        pending={юрлица.isPending}
        failed={юрлица.isError}
        failedError={юрлица.error as ApiError | null}
        emptyNote='Юридических лиц в справочнике нет. Их заводит администратор в настройках — без юрлица операция не проведётся.'
        options={(юрлица.data?.items ?? []).map((строка) => ({
          value: строка.id,
          label: строка.short_name ?? строка.name,
        }))}
        onChange={setЮрлицо}
      />

      <FilePicker
        id='claim-receipt'
        label='Снимок чека'
        подсказка='Выбрать снимок чека с диска'
        file={файл}
        onPick={setФайл}
        disabled={грузим}
      />

      {файл === null && (
        <label className='flex items-center gap-2 text-paragraph-sm text-text-sub-600'>
          <Switch.Root checked={безЧека} onCheckedChange={setБезЧека} />
          Чека нет: расход без подтверждающего документа
        </label>
      )}

      {запрет !== null && (
        <Alert.Root variant='lighter' status='warning' size='small'>
          <Alert.Icon />
          {запрет}
        </Alert.Root>
      )}
      {операция.error !== null && ошибкаДатыПоля === undefined && (
        <ErrorAlert error={операция.error as never} />
      )}

      {итог !== undefined && (
        <Alert.Root
          variant='lighter'
          status={итог.warning !== null ? 'warning' : 'success'}
          size='small'
        >
          <Alert.Icon />
          Заявка подана: {formatKopecks(итог.amount)}, {итог.category_title}. Сумма перешла в
          корзину «на рассмотрении» — на вас она пока висит, пока финотдел не примет.
          {/* Предупреждение сервера, а не наше рассуждение: закрытый расчёт получателя знает
              только он. Стоит ВНУТРИ плашки успеха, потому что заявка правда подана: отдельная
              красная плашка рядом читалась бы как отказ. */}
          {итог.warning !== null && <> {итог.warning}</>}
        </Alert.Root>
      )}

      <div className='flex gap-2'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {грузим ? 'Грузим чек…' : операция.isPending ? 'Подаём…' : 'Подать заявку'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          {итог !== undefined ? 'Готово' : 'Отмена'}
        </Button.Root>
      </div>
    </form>
  );
}
